import { arrayMove } from "@dnd-kit/sortable";

import {
  CommonLimitedListState,
  CommonPuppeteerLimitedListState,
} from "../../../types";

export type ListItem = (
  CommonLimitedListState | CommonPuppeteerLimitedListState
)["state"]["data"][number];

// One row edit: the replacement for what the row displays — its content,
// or its function call.
export type LimitedListItemEdit =
  | { content: string }
  | { function_call: { name: string; args: Record<string, unknown> } };

export const functionCallText = (fn: {
  name: string;
  args: Record<string, unknown>;
}): string =>
  `${fn.name}(${Object.keys(fn.args)
    .map((argName) => `${argName}=${JSON.stringify(fn.args[argName])}`)
    .join(", ")})`;

// A content row is one carrying a content string — the empty string too,
// which is what a freshly added blank message holds.
export const isContentItem = (item: ListItem): boolean =>
  typeof item.content === "string";

// What the row shows as text, and what the text filter matches: the
// content, or the call as name(arg=value, ...). Null for an item with
// neither, which has no row.
export const itemContentText = (item: ListItem): string | null =>
  isContentItem(item)
    ? item.content!
    : item.function_call
      ? functionCallText(item.function_call)
      : null;

// What the row's editor holds: content rows edit their text, function-call
// rows edit the call's JSON.
export const itemEditText = (item: ListItem): string | null =>
  isContentItem(item)
    ? item.content!
    : item.function_call
      ? JSON.stringify(item.function_call)
      : null;

// Only a {name, args} object round-trips back into a function-call item.
export const parseFunctionCallEdit = (
  text: string
): { name: string; args: Record<string, unknown> } | null => {
  let parsed: unknown;
  try {
    parsed = JSON.parse(text);
  } catch {
    return null;
  }
  if (
    parsed &&
    typeof parsed === "object" &&
    !Array.isArray(parsed) &&
    typeof (parsed as { name: unknown }).name === "string" &&
    typeof (parsed as { args: unknown }).args === "object" &&
    (parsed as { args: unknown }).args !== null &&
    !Array.isArray((parsed as { args: unknown }).args)
  ) {
    return parsed as { name: string; args: Record<string, unknown> };
  }
  return null;
};

// The full item order after one visible row moves to another's display
// position, as previous item indices. Filtered-out rows hold their slots;
// the visible rows permute among the visible slots.
export const orderAfterMove = (
  itemCount: number,
  visibleIndices: Array<number>,
  newestFirst: boolean,
  from: number,
  to: number
): Array<number> => {
  const display = newestFirst ? visibleIndices.toReversed() : visibleIndices;
  const nextDisplay = arrayMove(display, from, to);
  const nextVisible = newestFirst ? nextDisplay.toReversed() : nextDisplay;
  const order = Array.from({ length: itemCount }, (_, index) => index);
  visibleIndices.forEach((index, k) => {
    order[index] = nextVisible[k]!;
  });
  return order;
};
